import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { logSecurityEvent, getCachedUserId } from '@/utils/securityLogger';

interface Props {
  children: ReactNode;
  /** Current route path, used so the log identifies which page crashed. */
  routePath: string;
}

interface State {
  hasError: boolean;
  message: string | null;
  routeAtError: string | null;
}

/**
 * Route-level error boundary.
 * Catches a crash inside the routed page so the whole app does not blank out,
 * and logs it with the route path for admin diagnosis.
 */
class RouteErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: null, routeAtError: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error.message || 'Unknown error' };
  }

  componentDidUpdate(prevProps: Props) {
    // Recover automatically when the user navigates elsewhere.
    if (this.state.hasError && prevProps.routePath !== this.props.routePath) {
      this.setState({ hasError: false, message: null, routeAtError: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('error loading dynamically imported module');

    // Chunk errors are handled (and reloaded) by the root ChunkErrorBoundary.
    if (isChunkError) throw error;

    this.setState({ routeAtError: this.props.routePath });

    logSecurityEvent(
      'client_error_boundary',
      getCachedUserId(),
      `Page crashed at ${this.props.routePath}: ${(error.message || 'Unknown error').slice(0, 150)}`,
      {
        error_name: error.name,
        error_message: error.message?.slice(0, 300) || null,
        page_url: this.props.routePath,
        component_stack: errorInfo.componentStack?.slice(0, 500) || null,
        stack: error.stack?.slice(0, 300) || null,
        app_version: import.meta.env.VITE_APP_VERSION ?? null,
      }
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center space-y-4 max-w-sm">
            <p className="text-foreground font-medium">This page ran into a problem</p>
            <p className="text-muted-foreground text-sm">
              It has been reported automatically. You can retry or use the menu to go elsewhere.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, message: null, routeAtError: null })}
              className="px-4 py-3 min-h-[44px] bg-primary text-primary-foreground rounded-lg text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <RouteErrorBoundaryInner routePath={location.pathname}>{children}</RouteErrorBoundaryInner>;
}
